import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  FiBook,
  FiBriefcase,
  FiGlobe,
  FiLoader,
  FiAlertCircle,
  FiTrendingUp,
  FiTrendingDown,
  FiUser,
  FiHome,
  FiHeart,
  FiDollarSign,
  FiUsers,
  FiChevronDown,
  FiBarChart2,
  FiZap,
  FiHelpCircle,
  FiInfo,
} from "react-icons/fi";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../context/AuthContext";

const CATEGORIES = ["Family", "Friends", "Society", "Academics", "Teachers", "Self", "Health", "Money"] as const;
type Category = (typeof CATEGORIES)[number];

const CATEGORY_MATCH: Record<Category, string[]> = {
  Family: ["family"],
  Friends: ["friend"],
  Society: ["society", "environment", "surround"],
  Academics: ["academic"],
  Teachers: ["teacher"],
  Self: ["self"],
  Health: ["health", "fitness"],
  Money: ["money"],
};

const CAT_COLOR: Record<Category, string> = {
  Family: "#eab308",
  Friends: "#3b82f6",
  Society: "#0891b2",
  Academics: "#7c3aed",
  Teachers: "#dc2626",
  Self: "#ec4899",
  Health: "#10b981",
  Money: "#f59e0b",
};

const CAT_ICON: Record<Category, React.ReactNode> = {
  Family: <FiHome />,
  Friends: <FiUsers />,
  Society: <FiGlobe />,
  Academics: <FiBook />,
  Teachers: <FiBriefcase />,
  Self: <FiUser />,
  Health: <FiHeart />,
  Money: <FiDollarSign />,
};

const ZONES = [
  { key: "green",   label: "Green Zone",  sublabel: "Thriving",    color: "#10b981" },
  { key: "blue",    label: "Blue Zone",   sublabel: "Stable",      color: "#3b82f6" },
  { key: "yellow",  label: "Yellow Zone", sublabel: "Watch",       color: "#eab308" },
  { key: "brown",   label: "Brown Zone",  sublabel: "Warning",     color: "#92400e" },
  { key: "red",     label: "Red Zone",    sublabel: "Risk",        color: "#ef4444" },
  { key: "darkred", label: "Dark Red",    sublabel: "Severe Risk", color: "#7f1d1d" },
];

type ComparisonScope = "school" | "ensightly";

// Period -> benchmarking API period (same mapping as ParentWellbeing.tsx)
const PERIOD_MAP: Record<string, string> = {
  week:     "last_month",
  month:    "last_month",
  quarter:  "last_quarter",
  year:     "year",
  all_time: "all_time",
};

interface BenchmarkCategory {
  category: string;
  score: number;
}

interface BenchmarkState {
  loading: boolean;
  categories: BenchmarkCategory[] | null;
  error: string | null;
}

function matchBenchmarkCategory(benchmarkCats: BenchmarkCategory[], uiCategory: Category): number | null {
  if (!benchmarkCats || benchmarkCats.length === 0) return null;
  const keywords = CATEGORY_MATCH[uiCategory];
  const found = benchmarkCats.find((bc) => {
    const name = (bc.category || "").toLowerCase();
    return keywords.some((k) => name.includes(k));
  });
  if (found == null) return null;
  return found.score > 0 ? found.score : null;
}

function classifyLog(log: any): Category | null {
  const combined = ((log.category?.name || "") + " " + (log.subCategory?.name || "")).toLowerCase();
  for (const cat of CATEGORIES) {
    if (CATEGORY_MATCH[cat].some((t) => combined.includes(t))) return cat;
  }
  return null;
}

function normalizeZoneKey(zone: string): string {
  return (zone || "").toLowerCase().replace(" zone", "").replace(" ", "");
}

// Scoring formula CONSISTENT with benchmark API (schoolBenchmarkingController.js calcScoreStats):
// score = round(AVG(joyfulness) / 7 * 100)
function calcJoyScore(joySum: number, count: number): number {
  if (count === 0) return 0;
  return Math.round((joySum / count / 7) * 100);
}

function getCategoryStatus(score: number): { label: string; color: string } {
  if (score >= 80) return { label: "Thriving", color: "#10b981" };
  if (score >= 65) return { label: "Stable", color: "#3b82f6" };
  if (score >= 50) return { label: "Watch", color: "#eab308" };
  if (score >= 35) return { label: "Warning", color: "#92400e" };
  if (score >= 20) return { label: "Risk", color: "#ef4444" };
  return { label: "Severe Risk", color: "#7f1d1d" };
}

interface ZoneCounts {
  green: number; blue: number; yellow: number;
  brown: number; red: number; darkred: number;
  [key: string]: number;
}

const emptyZoneCounts = (): ZoneCounts => ({ green: 0, blue: 0, yellow: 0, brown: 0, red: 0, darkred: 0 });

interface ProcessedData {
  totalLogs: number;
  averageMood: number;
  overallZones: ZoneCounts;
  categoryZones: Record<Category, ZoneCounts>;
  categoryLogCount: Record<Category, number>;
  categoryJoySum: Record<Category, number>;
  categoryJoyCount: Record<Category, number>;
}

function processLogs(logs: any[]): ProcessedData {
  let count = 0;
  let overallJoySum = 0;
  const overallZones = emptyZoneCounts();
  const categoryZones: Record<Category, ZoneCounts> = {
    Family: emptyZoneCounts(), Friends: emptyZoneCounts(),
    Society: emptyZoneCounts(), Academics: emptyZoneCounts(),
    Teachers: emptyZoneCounts(), Self: emptyZoneCounts(),
    Health: emptyZoneCounts(), Money: emptyZoneCounts(),
  };
  const categoryLogCount: Record<Category, number> = {
    Family: 0, Friends: 0, Society: 0, Academics: 0, Teachers: 0, Self: 0, Health: 0, Money: 0,
  };
  const categoryJoySum: Record<Category, number> = {
    Family: 0, Friends: 0, Society: 0, Academics: 0, Teachers: 0, Self: 0, Health: 0, Money: 0,
  };
  const categoryJoyCount: Record<Category, number> = {
    Family: 0, Friends: 0, Society: 0, Academics: 0, Teachers: 0, Self: 0, Health: 0, Money: 0,
  };

  for (const log of logs) {
    const cat = classifyLog(log);
    if (!cat) continue;
    const zk = normalizeZoneKey(log.calculatedZone || "");
    if (!zk || !(zk in overallZones)) continue;
    count++;
    overallZones[zk]++;
    categoryZones[cat][zk]++;
    categoryLogCount[cat]++;
    if (log.joyfulness != null) {
      const joy = Number(log.joyfulness);
      overallJoySum += joy;
      categoryJoySum[cat] += joy;
      categoryJoyCount[cat]++;
    }
  }

  return {
    totalLogs: count,
    averageMood: count > 0 ? Math.round((overallJoySum / count) * 10) / 10 : 0,
    overallZones,
    categoryZones,
    categoryLogCount,
    categoryJoySum,
    categoryJoyCount,
  };
}

const FilterDropdown: React.FC<{
  label: string; value: string; options: string[];
  onChange: (v: string) => void; disabled?: boolean; disabledNote?: string;
}> = ({ label, value, options, onChange, disabled = false, disabledNote }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="flex flex-col gap-1.5" ref={ref}>
      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
        {label}
        {disabled && disabledNote && (
          <span className="text-[9px] font-bold text-amber-500 normal-case tracking-normal">({disabledNote})</span>
        )}
      </label>
      <div className="relative">
        <button
          onClick={() => { if (!disabled) setOpen(!open); }}
          disabled={disabled}
          className={`flex items-center justify-between gap-2 w-full min-w-[130px] px-3.5 py-2.5 border rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm ${
            disabled ? "bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed"
                     : "bg-white border-slate-200 text-slate-700 hover:border-[#1ecab8]/40"
          }`}
        >
          <span>{disabled ? "Unavailable" : value}</span>
          <FiChevronDown className={`text-slate-400 transition-transform duration-200 shrink-0 ${open ? "rotate-180" : ""}`} size={14} />
        </button>
        {open && !disabled && (
          <div className="absolute top-[calc(100%+6px)] left-0 min-w-full bg-white border border-slate-100 shadow-xl rounded-xl z-50 overflow-hidden">
            <div className="p-1">
              {options.map((opt) => (
                <button key={opt} onClick={() => { onChange(opt); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    value === opt ? "bg-[#1ecab8]/10 text-[#1ecab8] font-bold" : "text-slate-700 hover:bg-slate-50 font-medium"
                  }`}>{opt}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MoreChildrenDropdown: React.FC<{
  hiddenStudents: any[]; selectedChildId: number | null; onSelect: (id: number) => void;
}> = ({ hiddenStudents, selectedChildId, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const isActiveInHidden = hiddenStudents.some(s => s.id === selectedChildId);
  const activeHiddenChild = hiddenStudents.find(s => s.id === selectedChildId);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all duration-200 ${
          isActiveInHidden ? "bg-[#1ecab8] border-[#1ecab8] text-white shadow-md shadow-[#1ecab8]/20"
                           : "bg-white border-slate-200 text-slate-600 hover:border-[#1ecab8]/40"
        }`}>
        <span className="text-sm font-bold">{isActiveInHidden ? activeHiddenChild?.firstName : `+${hiddenStudents.length} More`}</span>
        <FiChevronDown size={13} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="absolute top-[110%] left-0 w-44 bg-white border border-slate-100 shadow-xl rounded-xl overflow-hidden z-50">
          <div className="p-1">
            {hiddenStudents.map(s => (
              <div key={s.id} onClick={() => { onSelect(s.id); setIsOpen(false); }}
                className={`px-3 py-2.5 rounded-lg cursor-pointer transition-colors text-sm font-semibold ${
                  selectedChildId === s.id ? "bg-[#1ecab8]/10 text-[#1ecab8]" : "hover:bg-slate-50 text-slate-700"
                }`}>{s.firstName} {s.lastName}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ScoreRing: React.FC<{ score: number; color: string; size?: number }> = ({ score, color, size = 80 }) => {
  const r = 28;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth="5" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth="5"
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.8s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-black text-slate-800" style={{ lineHeight: 1 }}>{score}</span>
        <span className="text-[9px] text-slate-400 font-semibold">/100</span>
      </div>
    </div>
  );
};

const CategoryCard: React.FC<{
  cat: Category;
  zones: ZoneCounts;
  total: number;
  logCount: number;
  joySum: number;
  joyCount: number;
  comparisonScope: ComparisonScope;
  benchmarkScore: number | null;
  benchmarkLoading: boolean;
  benchmarkLabel: string;
}> = ({ cat, zones, total, logCount, joySum, joyCount, comparisonScope, benchmarkScore, benchmarkLoading, benchmarkLabel }) => {

  // Child score: AVG(joyfulness) / 7 * 100 — same formula as benchmark API
  const score = calcJoyScore(joySum, joyCount);
  const status = getCategoryStatus(score);
  const pct = total > 0 ? Math.round((logCount / total) * 100) : 0;

  const dominantZone = logCount > 0
    ? ZONES.reduce((best, z) => zones[z.key] > zones[best.key] ? z : best, ZONES[0])
    : null;

  let comparisonUI: React.ReactNode;

  if (logCount === 0) {
    comparisonUI = <p className="text-[10px] text-slate-300 font-semibold text-center">No comparison available</p>;
  } else if (benchmarkLoading) {
    comparisonUI = (
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-slate-400 font-semibold">vs {benchmarkLabel}</span>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 animate-pulse">Loading…</span>
      </div>
    );
  } else if (benchmarkScore === null) {
    comparisonUI = (
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-slate-400 font-semibold">vs {benchmarkLabel}</span>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">No comparison data</span>
      </div>
    );
  } else {
    // Both on the same 0-100 joyfulness scale — comparison is mathematically valid
    const diff = score - benchmarkScore;
    const TOLERANCE = 2; // ±2 pts shown as "on par" to avoid +0.1% noise

    let compStatusText: string;
    let compStatusColor: string;
    let badgeBg: string;

    if (Math.abs(diff) <= TOLERANCE) {
      compStatusText = "On par";
      compStatusColor = "#64748b";
      badgeBg = "#f1f5f9";
    } else if (diff > 0) {
      compStatusText = `+${diff.toFixed(0)} pts above`;
      compStatusColor = "#10b981";
      badgeBg = "#dcfce7";
    } else {
      compStatusText = `${diff.toFixed(0)} pts below`;
      compStatusColor = "#ef4444";
      badgeBg = "#fee2e2";
    }

    comparisonUI = (
      <div className="space-y-2">
        {/* Header Row: vs Benchmark label & relative difference badge */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-400 font-semibold truncate max-w-[130px]" title={`vs ${benchmarkLabel}`}>
            vs {benchmarkLabel}
          </span>
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: badgeBg, color: compStatusColor }}
          >
            {compStatusText}
          </span>
        </div>

        {/* Score Bar with Benchmark Marker */}
        <div className="relative pt-0.5 pb-0.5">
          {/* Progress Track */}
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden relative">
            {/* Student Score Fill using student's actual zone color */}
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${Math.min(100, Math.max(0, score))}%`,
                background: status.color,
              }}
            />
          </div>

          {/* Benchmark Target Line / Pin */}
          {benchmarkScore > 0 && benchmarkScore <= 100 && (
            <div
              className="absolute top-0 bottom-0 flex flex-col items-center pointer-events-none"
              style={{ left: `${Math.min(98, Math.max(2, benchmarkScore))}%` }}
              title={`Benchmark Average: ${benchmarkScore}/100`}
            >
              <div className="w-0.5 h-full bg-slate-900 shadow-xs" />
            </div>
          )}
        </div>

        {/* Numerical comparison legend */}
        <div className="flex items-center justify-between text-[10px] font-medium text-slate-500">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ background: status.color }} />
            <span>Child: <strong>{score}</strong>/100</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-0.5 bg-slate-800" />
            <span>{comparisonScope === "school" ? "School Avg" : "Ensightly Avg"}: <strong>{benchmarkScore}</strong>/100</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col gap-4 hover:-translate-y-1 hover:shadow-md transition-all duration-300 group">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0"
            style={{ background: `${CAT_COLOR[cat]}18`, color: CAT_COLOR[cat] }}>
            {CAT_ICON[cat]}
          </div>
          <div>
            <p className="text-sm font-black text-slate-800">{cat}</p>
            <p className="text-[10px] font-semibold" style={{ color: dominantZone?.color || "#94a3b8" }}>
              {dominantZone ? dominantZone.sublabel : "No data"}
            </p>
          </div>
        </div>
      </div>

      {logCount === 0 ? (
        <div className="flex flex-col items-center py-3 gap-2">
          <div className="w-20 h-20 rounded-full border-4 border-dashed border-slate-100 flex items-center justify-center">
            <span className="text-slate-300 text-xs font-bold">–</span>
          </div>
          <p className="text-xs text-slate-300 font-semibold">No data</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1">
          <ScoreRing score={score} color={status.color} />
          <p className="text-[10px] font-semibold text-slate-400">Wellbeing Score</p>
        </div>
      )}

      <div>
        <div className="flex justify-between items-center mb-1.5">
          <span className="text-[10px] font-bold text-slate-400">Activity</span>
          <span className="text-[10px] font-bold text-slate-500">{logCount} logs · {pct}%</span>
        </div>
        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700 bg-slate-400"
            style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="pt-2 border-t border-slate-50">
        {comparisonUI}
      </div>

      {logCount > 0 && (
        <div className="flex justify-center">
          <span className="text-[10px] font-black px-3 py-1 rounded-full border"
            style={{ color: status.color, borderColor: `${status.color}40`, background: `${status.color}10` }}>
            {status.label}
          </span>
        </div>
      )}
    </div>
  );
};

const CategoryInsights: React.FC = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState<any[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<"week" | "month" | "quarter" | "year" | "all_time">("all_time");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentData, setCurrentData] = useState<ProcessedData | null>(null);
  const [prevData, setPrevData] = useState<ProcessedData | null>(null);

  const [comparisonScope, setComparisonScope] = useState<ComparisonScope>("school");
  const [filterGrade, setFilterGrade] = useState("All Grades");
  const [filterBoard, setFilterBoard] = useState("All Boards");
  const [filterCountry, setFilterCountry] = useState("All Countries");
  const [filterState, setFilterState] = useState("All States");
  // filterArea is kept in state for future use but NOT sent to API (backend ignores it)
  const [filterArea, setFilterArea] = useState("All Areas");

  const [benchmarkState, setBenchmarkState] = useState<BenchmarkState>({
    loading: false,
    categories: null,
    error: null,
  });

  // Static UI configuration — these are dropdown options, NOT hardcoded benchmark values
  const gradeOptions  = ["All Grades","Grade 1","Grade 2","Grade 3","Grade 4","Grade 5","Grade 6","Grade 7","Grade 8","Grade 9","Grade 10","Grade 11","Grade 12"];
  const boardOptions  = ["All Boards","CBSE","ICSE","State Board","IB","Cambridge"];
  const countryOptions= ["All Countries","India"];
  const stateOptions  = ["All States","Tamil Nadu","Maharashtra","Karnataka","Delhi","Kerala","Telangana","Andhra Pradesh","Gujarat"];
  const areaOptions   = ["All Areas","Urban","Semi-Urban","Rural","Mumbai","Pune","Chennai","Bangalore","Delhi","Kochi"];

  // Fetch children list
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/parent-auth/children`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const fetched = (res.data?.data || []).filter(
          (s: any) => !s.firstName?.toLowerCase().includes("mock") && !s.lastName?.toLowerCase().includes("external")
        );
        setStudents(fetched);
        if (fetched.length > 0) setSelectedChildId(fetched[0].id);
      } catch {
        console.error("[CategoryInsights] Failed to fetch children from API.");
        setStudents([]);
      }
    };
    fetchStudents();
  }, []);

  const getDateRange = (period: typeof selectedPeriod, offset = 0) => {
    const today = new Date();
    const from = new Date();
    const to = new Date();
    if (offset === 0) {
      switch (period) {
        case "week":    from.setDate(today.getDate() - 7); break;
        case "month":   from.setMonth(today.getMonth() - 1); break;
        case "quarter": from.setMonth(today.getMonth() - 3); break;
        case "year":    from.setFullYear(today.getFullYear() - 1); break;
        case "all_time": from.setFullYear(2000); break;
      }
      return { dateFrom: from.toISOString().split("T")[0], dateTo: today.toISOString().split("T")[0] };
    } else {
      if (period === "all_time") return null;
      switch (period) {
        case "week":    to.setDate(today.getDate() - 7); from.setDate(today.getDate() - 14); break;
        case "month":   to.setMonth(today.getMonth() - 1); from.setMonth(today.getMonth() - 2); break;
        case "quarter": to.setMonth(today.getMonth() - 3); from.setMonth(today.getMonth() - 6); break;
        case "year":    to.setFullYear(today.getFullYear() - 1); from.setFullYear(today.getFullYear() - 2); break;
      }
      return { dateFrom: from.toISOString().split("T")[0], dateTo: to.toISOString().split("T")[0] };
    }
  };

  // Fetch child mood logs
  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const curRange = getDateRange(selectedPeriod, 0)!;
        const prevRange = getDateRange(selectedPeriod, 1);
        if (!selectedChildId) { setCurrentData(processLogs([])); setPrevData(processLogs([])); setLoading(false); return; }
        const ids = [selectedChildId];

        const fetchLogsFor = async (range: { dateFrom: string; dateTo: string } | null) => {
          if (!range) return [];
          const all: any[] = [];
          await Promise.all(ids.map(async (id) => {
            try {
              const res = await axios.get(`${API_BASE_URL}/student-mood-logs`, {
                params: { studentId: id, status: true, dateFrom: range.dateFrom, dateTo: range.dateTo },
                signal: controller.signal,
              });
              all.push(...(res.data || []));
            } catch (e: any) { if (axios.isCancel(e)) throw e; }
          }));
          return all;
        };

        const [curLogs, prevLogs] = await Promise.all([fetchLogsFor(curRange), fetchLogsFor(prevRange)]);
        setCurrentData(processLogs(curLogs));
        setPrevData(processLogs(prevLogs));
      } catch (e: any) {
        if (!axios.isCancel(e)) { console.error("[CategoryInsights]:", e); setError("Failed to load insights."); }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchData();
    return () => controller.abort();
  }, [selectedChildId, selectedPeriod, students]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch benchmark from existing API: GET /api/schools/analytics/benchmarking
  // Same endpoint used by ParentWellbeing.tsx.
  // AbortController prevents stale responses from previous filter states.
  useEffect(() => {
    const controller = new AbortController();

    const fetchBenchmark = async () => {
      if (!selectedChildId || students.length === 0) {
        setBenchmarkState({ loading: false, categories: null, error: null });
        return;
      }
      setBenchmarkState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const activeChild = students.find((s) => s.id === selectedChildId);
        const apiPeriod = PERIOD_MAP[selectedPeriod] || "all_time";
        const params: Record<string, string> = { type: "external", period: apiPeriod };

        if (comparisonScope === "school") {
          if (activeChild?.schoolId) params.schoolId = String(activeChild.schoolId);
          if (activeChild?.id) params.excludeStudentId = String(activeChild.id);
          if (filterGrade !== "All Grades") {
            const gradeNum = filterGrade.replace(/[^0-9]/g, "");
            params.schoolGrade = gradeNum || filterGrade;
            params.avgGrade   = gradeNum || filterGrade;
          }
          if (filterBoard !== "All Boards") {
            params.schoolBoard = filterBoard;
            params.avgBoard    = filterBoard;
          }
        } else {
          // Ensightly — supports Country, State, Area, Board, Grade
          if (filterGrade !== "All Grades") {
            const gradeNum = filterGrade.replace(/[^0-9]/g, "");
            params.avgGrade = gradeNum || filterGrade;
          }
          if (filterBoard   !== "All Boards")    params.avgBoard   = filterBoard;
          if (filterCountry !== "All Countries") params.avgCountry = filterCountry;
          if (filterState   !== "All States")    params.avgState   = filterState;
          if (filterArea    !== "All Areas")     params.avgArea    = filterArea;
        }

        const res = await axios.get(`${API_BASE_URL}/schools/analytics/benchmarking`, {
          params,
          signal: controller.signal,
        });

        let cats: BenchmarkCategory[] | null =
          comparisonScope === "school"
            ? res.data?.school?.categories || null
            : res.data?.ensightly?.categories || null;

        if (!Array.isArray(cats) || cats.length === 0) cats = null;
        setBenchmarkState({ loading: false, categories: cats, error: null });

      } catch (e: any) {
        if (axios.isCancel(e)) return;
        console.error("[CategoryInsights benchmark]:", e);
        setBenchmarkState({ loading: false, categories: null, error: "Comparison unavailable" });
      }
    };

    fetchBenchmark();
    return () => controller.abort();
  }, [selectedChildId, selectedPeriod, comparisonScope, filterGrade, filterBoard, filterCountry, filterState, filterArea, students]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeChild     = students.find((s) => s.id === selectedChildId) || null;
  const activeChildName = activeChild ? `${activeChild.firstName} ${activeChild.lastName}` : "Student";
  const benchmarkLabel  = comparisonScope === "school" ? "School Average" : "Ensightly Average";

  const periodLabel = useMemo(() => {
    switch (selectedPeriod) {
      case "week":    return "Last Week";
      case "month":   return "Last Month";
      case "quarter": return "Last Quarter";
      case "year":    return "Last Year";
      case "all_time":return "All Time";
    }
  }, [selectedPeriod]);

  // AI insights — uses joyfulness-based scores (same formula as benchmark)
  const aiInsights = useMemo(() => {
    if (!currentData || currentData.totalLogs === 0) return null;
    const data = currentData;
    const catScores = CATEGORIES.map((cat) => ({
      cat,
      score: calcJoyScore(data.categoryJoySum[cat], data.categoryJoyCount[cat]),
      logs: data.categoryLogCount[cat],
    })).filter((c) => c.logs > 0);
    if (catScores.length === 0) return null;
    catScores.sort((a, b) => b.score - a.score);
    const best  = catScores[0];
    const worst = catScores[catScores.length - 1];
    const positivePct = data.totalLogs > 0
      ? Math.round(((data.overallZones.green + data.overallZones.blue) / data.totalLogs) * 100)
      : 0;
    return { best, worst, positivePct, bestStatus: getCategoryStatus(best.score), worstStatus: getCategoryStatus(worst.score) };
  }, [currentData]);

  if (loading && !currentData) {
    return (
      <div className="min-h-screen bg-[#F6F8F9] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FiLoader className="text-[#1ecab8] animate-spin" size={36} />
          <p className="text-slate-400 text-sm font-semibold">Loading insights…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F6F8F9] flex items-center justify-center">
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-200 max-w-sm">
          <FiAlertCircle className="mx-auto text-red-400 mb-4" size={40} />
          <p className="text-slate-800 font-black text-lg mb-1">Unable to load insights</p>
          <p className="text-slate-500 text-sm mb-6">We couldn't retrieve the wellbeing data right now.</p>
          <button onClick={() => window.location.reload()}
            className="px-5 py-2.5 bg-[#1ecab8] text-white rounded-xl font-bold hover:bg-[#17a99a] transition-colors text-sm">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const data = currentData!;

  return (
    <div className="min-h-screen bg-[#F6F8F9] text-slate-800 font-sans pb-24">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-8">

        {/* SECTION A: HEADER */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <FiBarChart2 className="text-[#1ecab8]" size={18} />
                <p className="text-[10px] font-black text-[#1ecab8] uppercase tracking-widest">Category Insights</p>
              </div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight mb-1">Individual Deep Dive</h1>
              <p className="text-sm text-slate-500">
                Detailed wellbeing analysis across all life circles for{" "}
                <strong className="text-slate-700">{activeChildName}</strong>
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {activeChild && (
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 px-3 py-2 rounded-xl">
                  <div className="w-7 h-7 rounded-lg bg-[#1ecab8]/10 text-[#1ecab8] flex items-center justify-center text-xs font-black">
                    {activeChild.firstName?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <p className="text-xs font-black text-slate-700">{activeChildName}</p>
                    <p className="text-[10px] text-slate-400 font-semibold">
                      {activeChild.className || "Student"}
                      {activeChild.boardName ? ` · ${activeChild.boardName}` : ""}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-100">
                <div className="w-1.5 h-1.5 rounded-full bg-[#1ecab8] animate-pulse" />
                <span className="text-[10px] font-semibold text-slate-400">Live · {periodLabel}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-5 pt-5 border-t border-slate-50">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest shrink-0">Child:</span>
              <div className="flex items-center gap-2 flex-wrap">
                {students.slice(0, 3).map((s) => {
                  const isActive = selectedChildId === s.id;
                  return (
                    <button key={s.id} onClick={() => setSelectedChildId(s.id)}
                      className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border transition-all duration-200 ${
                        isActive ? "bg-[#1ecab8] border-[#1ecab8] text-white shadow-md shadow-[#1ecab8]/20"
                                 : "bg-white border-slate-200 text-slate-600 hover:border-[#1ecab8]/40"
                      }`}>
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-black shrink-0 ${
                        isActive ? "bg-white/20 text-white" : "bg-[#1ecab8]/10 text-[#1ecab8]"
                      }`}>{s.firstName?.[0]?.toUpperCase()}</div>
                      <span className="text-sm font-bold">{s.firstName}</span>
                    </button>
                  );
                })}
                {students.length > 3 && (
                  <MoreChildrenDropdown hiddenStudents={students.slice(3)} selectedChildId={selectedChildId} onSelect={setSelectedChildId} />
                )}
                {students.length === 0 && <span className="text-xs text-slate-400 font-semibold">No children found</span>}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest shrink-0">Period:</span>
              <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
                {(["week", "month", "quarter", "year", "all_time"] as const).map((p) => (
                  <button key={p} onClick={() => setSelectedPeriod(p)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all duration-200 whitespace-nowrap ${
                      selectedPeriod === p ? "bg-[#1ecab8] text-white shadow-sm shadow-[#1ecab8]/30" : "text-slate-500 hover:text-slate-700"
                    }`}>
                    {p === "all_time" ? "All Time" : p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          {loading && (
            <div className="absolute inset-0 bg-[#F6F8F9]/80 backdrop-blur-sm flex items-center justify-center z-20 rounded-2xl">
              <FiLoader className="text-[#1ecab8] animate-spin" size={32} />
            </div>
          )}

          {/* SECTION C: OVERALL ZONE DISTRIBUTION */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Overall Zone Distribution</p>
                <h2 className="text-base font-black text-slate-800">
                  How your child's wellbeing has been distributed across zones during this period
                </h2>
              </div>
              <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100 shrink-0 ml-4">
                {data.totalLogs} total logs
              </span>
            </div>

            {data.totalLogs === 0 ? (
              <div className="text-center py-10">
                <FiAlertCircle className="mx-auto text-slate-200 mb-3" size={36} />
                <p className="text-slate-400 font-semibold text-sm">No wellbeing data yet</p>
                <p className="text-slate-300 text-xs mt-1">There isn't enough data for this period. Try selecting a wider time range.</p>
              </div>
            ) : (
              <>
                <div className="flex h-10 w-full rounded-xl overflow-hidden mb-4 gap-0.5">
                  {ZONES.map((z) => {
                    const pct = data.totalLogs > 0 ? (data.overallZones[z.key] / data.totalLogs) * 100 : 0;
                    if (pct === 0) return null;
                    return (
                      <div key={z.key} title={`${z.label}: ${Math.round(pct)}%`}
                        className="flex items-center justify-center transition-all duration-700 hover:opacity-90 cursor-default"
                        style={{ width: `${pct}%`, background: z.color, minWidth: pct > 0 ? 2 : 0 }}>
                        {pct > 8 && <span className="text-white text-[10px] font-black drop-shadow">{Math.round(pct)}%</span>}
                      </div>
                    );
                  })}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {ZONES.map((z) => {
                    const count = data.overallZones[z.key];
                    const pct = data.totalLogs > 0 ? Math.round((count / data.totalLogs) * 100) : 0;
                    return (
                      <div key={z.key} className="flex items-start gap-2">
                        <div className="w-3 h-3 rounded-full mt-0.5 shrink-0" style={{ background: z.color }} />
                        <div>
                          <p className="text-xs font-bold text-slate-700">{z.label.replace(" Zone", "")}</p>
                          <p className="text-[10px] text-slate-400 font-semibold">{z.sublabel}</p>
                          <p className="text-sm font-black text-slate-800">{pct}%</p>
                          <p className="text-[10px] text-slate-400">{count} logs</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* SECTION B: COMPARISON CONFIGURATION */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Compare Wellbeing</p>
                <h2 className="text-base font-black text-slate-800">Choose the benchmark used to understand your child's wellbeing</h2>
              </div>
              <div className="flex items-center bg-slate-100 p-1 rounded-xl shrink-0">
                <button onClick={() => setComparisonScope("school")}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${
                    comparisonScope === "school" ? "bg-[#1ecab8] text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}>Inside School</button>
                <button onClick={() => setComparisonScope("ensightly")}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${
                    comparisonScope === "ensightly" ? "bg-[#1ecab8] text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}>Ensightly Average</button>
              </div>
            </div>

            {comparisonScope === "school" ? (
              <div className="flex flex-wrap gap-4">
                <FilterDropdown label="Grade" value={filterGrade} options={gradeOptions} onChange={setFilterGrade} />
                <FilterDropdown label="Board" value={filterBoard} options={boardOptions} onChange={setFilterBoard} />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <FilterDropdown label="Country" value={filterCountry} options={countryOptions} onChange={setFilterCountry} />
                <FilterDropdown label="State"   value={filterState}   options={stateOptions}   onChange={setFilterState} />
                <FilterDropdown label="Area" value={filterArea} options={areaOptions} onChange={setFilterArea} />
                <FilterDropdown label="Board" value={filterBoard} options={boardOptions} onChange={setFilterBoard} />
                <FilterDropdown label="Grade" value={filterGrade} options={gradeOptions} onChange={setFilterGrade} />
              </div>
            )}

            <div className="mt-4 flex items-center gap-2">
              <FiInfo size={11} className="text-slate-400 shrink-0" />
              {benchmarkState.loading ? (
                <p className="text-[10px] text-slate-400 font-semibold animate-pulse">Fetching benchmark data…</p>
              ) : benchmarkState.error ? (
                <p className="text-[10px] text-amber-500 font-semibold">{benchmarkState.error}</p>
              ) : benchmarkState.categories ? (
                <p className="text-[10px] text-emerald-600 font-semibold">
                  Benchmark loaded — {benchmarkState.categories.length} categories · same scoring scale as child scores (AVG joyfulness / 7 × 100).
                </p>
              ) : (
                <p className="text-[10px] text-slate-400 font-semibold">
                  {comparisonScope === "school"
                    ? "No school benchmark data available. Try adjusting grade or board filters."
                    : "No Ensightly benchmark data for the selected filters."}
                </p>
              )}
            </div>
          </div>

          {/* SECTION D: INDIVIDUAL CATEGORY CARDS */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Life Circle Analysis</p>
                <h2 className="text-base font-black text-slate-800">Individual performance across 8 key life areas</h2>
              </div>
              <span className="text-[10px] font-bold text-slate-400 bg-white px-3 py-1.5 rounded-lg border border-slate-100 shadow-sm">
                Comparing vs {comparisonScope === "school" ? "School" : "Ensightly"} · {filterGrade}
              </span>
            </div>

            {data.totalLogs === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
                <FiAlertCircle className="mx-auto text-slate-200 mb-3" size={40} />
                <p className="text-slate-600 font-black text-lg mb-1">No wellbeing data yet</p>
                <p className="text-slate-400 text-sm">There isn't enough wellbeing data for this period to generate category insights.</p>
                <p className="text-slate-300 text-xs mt-2">Try selecting another time period.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {CATEGORIES.map((cat) => {
                  const benchmarkScore = benchmarkState.categories
                    ? matchBenchmarkCategory(benchmarkState.categories, cat)
                    : null;
                  return (
                    <CategoryCard
                      key={cat}
                      cat={cat}
                      zones={data.categoryZones[cat]}
                      total={data.totalLogs}
                      logCount={data.categoryLogCount[cat]}
                      joySum={data.categoryJoySum[cat]}
                      joyCount={data.categoryJoyCount[cat]}
                      comparisonScope={comparisonScope}
                      benchmarkScore={benchmarkScore}
                      benchmarkLoading={benchmarkState.loading}
                      benchmarkLabel={benchmarkLabel}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* SECTION E: AI BEHAVIORAL INSIGHTS */}
          <div className="bg-gradient-to-br from-teal-50 via-white to-white rounded-2xl border border-teal-100 shadow-sm p-6">
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#1ecab8]/10 flex items-center justify-center">
                  <FiZap className="text-[#1ecab8]" size={18} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-base font-black text-slate-800">AI Behavioral Insights</p>
                    <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-[#1ecab8]/10 text-[#1ecab8] border border-[#1ecab8]/20 uppercase tracking-wider">Beta</span>
                  </div>
                  <p className="text-xs text-slate-500 font-semibold">AI-powered analysis of your child's wellbeing patterns</p>
                </div>
              </div>
              <button className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 hover:text-slate-600 transition-colors">
                <FiHelpCircle size={12} />
                How AI Insights Work
              </button>
            </div>

            {!aiInsights ? (
              <div className="text-center py-8">
                <FiZap className="mx-auto text-slate-200 mb-3" size={36} />
                <p className="text-slate-500 font-semibold text-sm">AI insights will appear here once enough wellbeing data is available.</p>
                <p className="text-slate-300 text-xs mt-1">Try selecting a wider time period.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                  <div className="bg-white rounded-xl border border-slate-100 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FiTrendingUp className="text-emerald-500" size={14} />
                      <p className="text-xs font-black text-slate-700">Key Strength</p>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Your child's strongest area is <strong>{aiInsights.best.cat}</strong> with a wellbeing score of{" "}
                      <strong>{aiInsights.best.score}/100</strong> — currently in the{" "}
                      <strong style={{ color: aiInsights.bestStatus.color }}>{aiInsights.bestStatus.label}</strong> zone.
                    </p>
                    <button className="mt-2.5 text-[10px] font-bold text-emerald-600 hover:text-emerald-700 transition-colors">
                      Maintain &amp; Celebrate →
                    </button>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-100 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FiTrendingDown className="text-amber-500" size={14} />
                      <p className="text-xs font-black text-slate-700">Growth Opportunity</p>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      <strong>{aiInsights.worst.cat}</strong> shows the most room for growth with a score of{" "}
                      <strong>{aiInsights.worst.score}/100</strong> — currently{" "}
                      <strong style={{ color: aiInsights.worstStatus.color }}>{aiInsights.worstStatus.label}</strong>.
                    </p>
                    <button className="mt-2.5 text-[10px] font-bold text-amber-600 hover:text-amber-700 transition-colors">
                      Focus &amp; Support →
                    </button>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-100 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FiBarChart2 className="text-[#1ecab8]" size={14} />
                      <p className="text-xs font-black text-slate-700">Overall Trend</p>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      {aiInsights.positivePct >= 60
                        ? `Excellent overall wellbeing! ${aiInsights.positivePct}% of time in positive zones (Green + Blue). Continue the great momentum.`
                        : aiInsights.positivePct >= 40
                          ? `Moderate wellbeing. ${aiInsights.positivePct}% of time in positive zones. Consider exploring support in key areas.`
                          : `Your child needs more support. Only ${aiInsights.positivePct}% in positive zones. A focused check-in is recommended.`
                      }
                    </p>
                    <button className="mt-2.5 text-[10px] font-bold text-[#1ecab8] hover:text-[#17a99a] transition-colors">
                      Keep Going Strong →
                    </button>
                  </div>
                </div>
                <div className="bg-[#1ecab8]/5 border border-[#1ecab8]/15 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-[#1ecab8]/10 flex items-center justify-center shrink-0">
                    <FiZap className="text-[#1ecab8]" size={13} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-[#1ecab8] uppercase tracking-widest mb-1">Suggested Next Step</p>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Consider having a short, open conversation with {activeChild?.firstName || "your child"} about{" "}
                      <strong>{aiInsights.worst.cat}</strong> — explore what's happening and what kind of support they might find helpful.
                      Celebrate their strength in <strong>{aiInsights.best.cat}</strong> to keep motivation high.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryInsights;
